'use client'

import {
    createSupplierType,
    getSupplierType,
    updateSupplierType,
} from '@/lib/api/suppliers/supplier-type-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function SupplierTypesFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [name, setName] = useState('')
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (isEditing && id) {
            setLoading(true)
            getSupplierType(parseInt(id)).then(res => {
                if (res.success) {
                    setName(res.result.name)
                } else {
                    toast.error('Tipo não encontrado')
                    navigate('/registrations/supplier-types')
                }
            }).finally(() => setLoading(false))
        }
    }, [id, isEditing, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateSupplierType(parseInt(id!), { name: name.trim() })
                : await createSupplierType({ name: name.trim(), organizationId: user!.organizationId! })

            if (response.success) {
                toast.success(isEditing ? 'Tipo atualizado com sucesso' : 'Tipo criado com sucesso')
                navigate('/registrations/supplier-types')
            } else {
                toast.error(response.error || 'Erro ao salvar tipo')
            }
        } catch {
            toast.error('Erro ao salvar tipo')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/supplier-types')}
                    className="btn btn-ghost btn-sm btn-circle"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                    {isEditing ? 'Editar Tipo de Fornecedor' : 'Novo Tipo de Fornecedor'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">Nome *</span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Ex: Distribuidora, Fabricante, Laboratório"
                            required
                            autoFocus
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/supplier-types')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar Alterações' : 'Criar Tipo'}
                    </button>
                </div>
            </form>
        </div>
    )
}
